<template>
  <div>
    <div class='p-4 border rounded-md mb-8'>
      <h1 class='text-2xl font-semibold mb-4 text-gray-900'>My Registration Details</h1>
      <registration-form-fields
        @update-phone-number-is-valid='updatePhoneNumberIsValid' />
    </div>
    <div class='border rounded-t-md mt-8'>
      <h1 class='text-2xl font-semibold mb-4 text-gray-900 p-4'>Registration Fee</h1>
      <div class='px-4'>
        <select
          v-model='registrationProduct'
          class='px-2 py-2 w-full border rounded-md'>
          <option v-for='product in filteredProducts'
            :key='`registration-product-${product.id}`' 
            :value='product'>
            {{ product.description }}: {{ product.price.priceUnit }} {{ priceString(product.price.amount) }}
          </option>
        </select>
      </div>
      <div class='bg-gray-100 text-gray-900 mt-8 px-4 py-6 uppercase font-semibold flex flex-row justify-between items-center'>
        <span>Total Amount Due:</span>
        <span>{{registrationFeeString}}</span>
      </div>
      <div class='border-l border-r border-b bg-gray-50'>
        <div v-if='isKorean && registrationFeePaymentRequired'
          class='flex flex-row justify-between items-center px-4 py-2'>
          <div class='uppercase'>Payment Method</div>
          <select
            v-model='paymentGateway'
            class='border solid rounded-md px-2 py-3'>
            <option value='iamport-nice'>Korean Domestic Card (신용카드)</option>
          </select>
        </div>
        <div v-if='isPaymentGatewayBankTransfer'>
          <div class='flex flex-row justify-between items-center border-t mx-4 pt-2 pb-4'>
            <label class='uppercase'>입금자명</label>
            <input
              v-model='registrationData.depositor'
              class='border py-2 px-2 rounded block w-64'
              type='text'
              placeholder='입금자명'>
          </div>
          <div class='flex flex-row justify-between items-center border-t mx-4 pt-2 pb-4'>
            <label class='uppercase'>입금예정일</label>
            <date-picker
              class='w-auto date-picker-auto-width'
              input-class='border py-2 px-2 rounded block w-64'
              v-model="registrationData.expectedDepositDate"
              valueType="format"
              placeholder='입금예정일'>
            </date-picker>
          </div>
        </div>
      </div>
    </div>
    <div class='px-4 py-8 bg-gray-50 border-l border-r border-b rounded-b-md mb-8'>
      <h1 class='text-base font-semibold mb-4 text-gray-700 uppercase'>Cancellation & Refund Policy</h1>
      <ul class='list-disc pl-4 text-sm'>
        <li>Cancellations must be notified to the secretariat in writing by email to: <a href='mailto:ortho@koa.or.kr' class='underline text-blue-700'>ortho@koa.or.kr</a></li>
        <li>All refunds will be made after the congress.</li>
        <li>All bank service charges and all administration fees will be deducted from all congress registration refunds.</li>
        <li>Please refer to the following cut off dates for cancellation.</li>
      </ul>
    </div>
    <button
      :disabled='disabledRegistrationButton'
      @click='saveDataAndRegister'
      class='border py-4 px-8 text-center text-white text-sm uppercase w-full block rounded-md'
      :style='`background-color: ${eventMainThemeColor};`'
      :class='disabledRegistrationButtonClass'>
      Register & Pay Registration Fee
    </button>
    <ul class='list-disc list-inside mt-8 text-red-600 text-xs'>
      <li v-if='emptyMedicalProfession'>Please enter your medical profession</li>
      <li v-if='emptyTitle && !isKorean'>Please enter your title</li>
      <li v-if='emptyFirstName'>Please enter your first & middle name</li>
      <li v-if='emptylastName'>Please enter your last name</li>
      <li v-if='emptyName && isKorean'>성명 (국문)을 입력해주세요</li>
      <li v-if='emptyLicenseNumber && isKorean'>Please enter your license number</li>
      <li v-if='emptyHospitalName && isKorean'>Affiliation (국문)을 입력해주세요</li>
      <li v-if='emptyOrganization'>Please enter your affiliation</li>
      <li v-if='emptyDepartment'>Please enter your department</li>
      <li v-if='emptyParticipationType'>Please enter your participation type</li>
      <li v-if='emptyParticipantType'>Please enter your participant type</li>
      <li v-if='emptyNametagPickupLoaction && isKorean && isParticipationTypeOffline'>명찰수령방식을 선택해주세요</li>
      <li v-if='emptyAddress && isKorean && isParticipationTypeOffline  && isNametagPickupLocationTypeHome'>명찰수령주소를 입력해주세요</li>
      <li v-if='emptyAddressDetail && isKorean && isParticipationTypeOffline  && isNametagPickupLocationTypeHome'>명찰수령상세주소를 입력해주세요</li>
      <li v-if='emptyPostalCode && isKorean && isParticipationTypeOffline && isNametagPickupLocationTypeHome'>Please enter your postcal code</li>
      <li v-if='emptyPhoneNumber'>Please enter your mobile phone number with international calling code</li>
      <li v-if='invalidPhoneNumber'>Please check your mobile phone number</li>
      <li v-if='emptyPaymentGateway && isKorean && registrationFeePaymentRequired'>Please select your payment gateway type</li>
      <li v-if='emptyRegistrationProduct'>Please select your registration type</li>
      <li v-if='emptyDepositor && isKorean && isPaymentGatewayBankTransfer'>입금자명을 입력해주세요</li>
      <li v-if='emptyExpectedDepositDate && isKorean && isPaymentGatewayBankTransfer'>입금예정일을 입력해주세요</li>
    </ul>
    <modal
      name='registration-payment-modal'
      :adaptive='true'
      :click-to-close='canClickToCloseRegistrationModal'
      :min-height='registrationModalMinHeight'
      :min-width='registrationModalMinWidth'
      :scrollable='true'
      classes='registration-payment-modal relative'
      @opened='initializePaymentModal'
      @closed='redirectIfFinished'>
      <button v-if='canClickToCloseRegistrationModal'
        class='z-30 absolute top-3 right-3' 
        @click="$modal.hide('registration-payment-modal')">
        <x-icon class='text-black' />
      </button>
      <div v-if='registrationPaymentModalComponent == "bank-transfer"'>
        <div class='p-8 lg:p-16 overflow-y-auto h-screen flex-grow'>
          <div class='max-w-2xl'>
            <h1 class='text-3xl font-semibold mb-8'>사전등록 확인</h1>
            <div class='px-12 py-8 rounded' style='background-color: #FAFAFA;'>
              <ul class='list-disc'>
                <li class='py-1'>등록비: {{registrationFeeString}}원</li>
                <li class='py-1'>등록비 납부는 온라인 사전등록을 완료하신 후 반드시 입금자 성함으로 송금해 주시고 현금영수증은 성함, 입금일, 현금영수증 발행번호를 작성하여 학회 이메일 (<a href='mailto:ortho5@koa.or.kr' class='underline' :style='themeTextColorStyle'>ortho5@koa.or.kr</a>)로 요청하시기 바랍니다.</li>
                <li class='py-1'>등록 내용은 접수완료 메일을 확인해 주시고 등록비 납부가 확인되면 최종 등록 완료 메일이 발송됩니다.</li>
                <li class='py-1'>학회 계좌 정보 : <span class='inline-block p-2 bg-gray-200 text-gray-900 rounded font-semibold'>KB국민은행 269101-04-008077</span>, 예금주 대한정형외과학회</li>
              </ul>
            </div>
            <router-link :to='{name: "Home"}' class='block mt-8 w-48 text-center bg-gray-900 text-white py-3 px-2 rounded-md hover:shadow-lg text-sm'>홈으로 가기</router-link>
          </div>
        </div>
      </div>
      <iamport-payment v-if='registrationPaymentModalComponent == "iamport-payment"'
        :payment-params-prop='paymentParams'
        :is-modal-payment='true'
        :is-single-order='true'
        :is-korean='isKorean'
        @payment-success='iamportSuccess'
        @payment-fail='paymentFail' />
      <payment-fail v-if='registrationPaymentModalComponent == "payment-fail"'
        :is-modal-payment='true'
        :is-korean='isKorean' />
      <payment-success v-if='registrationPaymentModalComponent == "payment-success"'
        :payment-params-prop='paymentSuccessParams'
        :is-modal-payment='true'
        :is-korean='isKorean'
        @payment-finished='paymentFinished'
        @payment-fail='paymentFail' />
      <payment-finished v-if='registrationPaymentModalComponent == "payment-finished"'
        :is-modal-payment='true'>
      </payment-finished>
    </modal>
  </div>
</template>

<script>
import { mapFields } from 'vuex-map-fields'
import { mapActions, mapState, mapGetters } from 'vuex'
import { XIcon } from 'vue-tabler-icons'
import RegistrationFormFields  from '@/components/RegistrationFormFields.vue'
import IamportPayment          from '@/components/iamport-payment/IamportPayment.vue'
import PaymentFail             from '@/components/payments/PaymentFail.vue'
import PaymentSuccess          from '@/components/payments/PaymentSuccess.vue'
import PaymentFinished         from '@/components/payments/PaymentFinished.vue'
import numbro from 'numbro'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'

export default {
  name: 'RegistrationForMyselfOnly',
  components: {
    RegistrationFormFields,
    IamportPayment,
    PaymentFail,
    PaymentSuccess,
    PaymentFinished,
    XIcon,
    DatePicker,
  },
  data () {
    return {
      paymentParams: null,
      registrationPaymentModalComponent: '',
      paymentSuccessParams: null,
      paymentFailParams: null,
      paymentFinishedParams: null,
      paymentGateway: 'iamport-nice',
      invalidPhoneNumber: false,
    }
  },
  watch: {
    'isKorean': {
      handler: function (newVal) {
        if (newVal) {
          this.paymentGateway = 'iamport-nice'
        } else {
          this.paymentGateway = 'iamport-eximbay'
        }
      },
      immediate: true 
    },
    'registrationProduct': {
      handler: function (newVal) {
        if (newVal) {
          if (newVal.name.includes('over65')) {
            this.membershipData.isKoaMember = 'is_member'
            this.membershipData.isKoaMemberOver65 = true
            this.paymentGateway = ''
          } else if (newVal.name.includes('trainee')) {
            this.membershipData.isKoaMember = 'is_member'
            this.membershipData.isKoaMemberOver65 = false
            this.paymentGateway = ''
          } else if (newVal.name === 'member') {
            this.membershipData.isKoaMemberOver65 = false
            this.membershipData.isKoaMember = 'is_member'
          } else {
            // nonMember, Resident, Nures, Army Doctor, Researcher
            this.membershipData.isKoaMemberOver65 = false
            this.membershipData.isKoaMember = 'not_member'
          }
        } 
      },
      immediate: true 
    },
    'filteredProducts.length': {
      handler: function (newVal) {
        if (newVal && newVal === 1) {
          this.registrationProduct = this.filteredProducts[0]
        } else if (newVal && newVal > 1) {
          let index = this.filteredProducts.findIndex(product => product.id === this.registrationData.productId)
          if (index > -1) {
            this.registrationProduct = this.filteredProducts[index]
          } else {
            this.registrationProduct = null
          }
        }
      },
      immediate: true 
    },  
  },
  computed: {
    ...mapFields('products', [
      'registrationProduct',
    ]),
    ...mapFields('users', [
      'membershipData',
      'registrationData',
    ]),
    ...mapState('users', [
      'hasValidToken',
      'profile',
    ]),
    ...mapState('products', [
      'products',
    ]),
    ...mapGetters('events', [
      'eventMainThemeColor',
      'themeTextColorStyle',
      'showingEventId',
    ]),
    backgroundStyle () {
      return `background-color: ${this.eventMainThemeColor};`
    },
    canClickToCloseRegistrationModal () {
      return this.registrationPaymentModalComponent == 'payment-fail'
    },
    registrationModalMinHeight () {
      return 800
    },
    registrationModalMinWidth () {
      return 700
    },
    registrationFeeUnit () {
      return (this.registrationProduct) ? this.registrationProduct.price.priceUnit : ''
    },
    registrationFee () {
      return (this.registrationProduct) ? this.registrationProduct.price.amount : 0
    },
    registrationFeeString () {
      return `${this.registrationFeeUnit} ${numbro(this.registrationFee).format({
        thousandSeparated: true,
      })}`
    },
    isKorean () {
      return !this.profile.country ||
             this.profile.country.toLowerCase() === 'kr'
    },
    emptyMedicalProfession () {
      return !this.profile.extraInfo.medicalProfession
    },
    emptyTitle () {
      return !this.profile.title
    },
    emptyFirstName () {
      return !this.profile.firstName
    },
    emptylastName () {
      return !this.profile.lastName
    },
    emptyName () {
      return !this.profile.name
    },
    emptyLicenseNumber () {
      return !this.profile.licenseNumber
    },
    emptyOrganization () {
      return !this.profile.organization
    },
    emptyHospitalName () {
      return !this.profile.hospitalName
    },
    emptyDepartment () {
      return !this.profile.department
    },
    emptyParticipationType () {
      return !this.registrationData.participationType
    },
    emptyParticipantType () {
      return !this.registrationData.participantType
    },
    emptyNametagPickupLoaction () {
      return !this.registrationData.nametagPickupLocation
    },
    emptyAddress () {
      return !this.profile.address
    },
    emptyAddressDetail () {
      return !this.profile.addressDetail
    },
    emptyPostalCode () {
      return !this.profile.postalCode
    },
    emptyPhoneNumber () {
      return !this.profile.phoneNumber
    },
    emptyPaymentGateway () {
      return !this.paymentGateway
    },
    emptyRegistrationProduct () {
      return !this.registrationProduct
    },
    emptyDepositor () {
      return !this.registrationData.depositor
    },
    emptyExpectedDepositDate () {
      return !this.registrationData.expectedDepositDate
    },
    isParticipationTypeOffline () {
      return this.registrationData.participationType.toLowerCase() === 'offline'
    },
    isNametagPickupLocationTypeHome () {
      return this.registrationData.nametagPickupLocation === 'home'
    },
    registrationFeePaymentRequired () {
      return !this.emptyRegistrationProduct && !(this.registrationProduct.name.includes('over65') || this.registrationProduct.name.includes('trainee'))
    },
    disabledRegistrationButton () {
      let emptyDefaultFields = this.emptyMedicalProfession ||
        this.emptyFirstName ||
        this.emptylastName ||
        this.emptyOrganization ||
        this.emptyDepartment ||
        this.emptyParticipationType ||
        this.emptyParticipantType ||
        this.emptyPhoneNumber ||
        this.invalidPhoneNumber ||
        this.emptyRegistrationProduct
      if (this.registrationFeePaymentRequired) {
        emptyDefaultFields = emptyDefaultFields || this.emptyPaymentGateway
      }
      if (this.isKorean) {
        emptyDefaultFields = emptyDefaultFields ||
          this.emptyName ||
          this.emptyLicenseNumber ||
          this.emptyHospitalName
        if (this.isParticipationTypeOffline && this.isNametagPickupLocationTypeHome) {
          emptyDefaultFields = emptyDefaultFields ||
          this.emptyAddress ||
          this.emptyAddressDetail ||
          this.emptyPostalCode
        }
        if (this.isPaymentGatewayBankTransfer) {
          emptyDefaultFields = emptyDefaultFields || this.emptyDepositor || this.emptyExpectedDepositDate
        }
        return emptyDefaultFields
      } else {
        return emptyDefaultFields || this.emptyTitle
      }
    },
    disabledRegistrationButtonClass () {
      return this.disabledRegistrationButton ? 'opacity-50 cursor-not-allowed' : ''
    },
    isPaymentGatewayBankTransfer () {
      return this.paymentGateway === 'bank_transfer'
    },
    filteredProducts () {
      if (this.isKorean) {
        if (this.membershipData.ageType === 'over65') {
          return this.products.filter(product => product.name.toLowerCase().includes('over65'))
        } else if (this.membershipData.ageType === 'trainee') {
          return this.products.filter(product => product.name.toLowerCase().includes('trainee'))
        } else {
          return this.products.filter(product => !product.name.toLowerCase().includes('international') &&
                                                 !product.name.toLowerCase().includes('over65') && 
                                                 !product.name.toLowerCase().includes('trainee'))
        }
      } else {
        return this.products.filter(product => product.name.toLowerCase().includes('international'))
      }
    },
    registrationNumber () {
      if (this.isKorean) {
        return `K-${this.profile.ssoIdentityId}`
      } else {
        return `F-${this.profile.ssoIdentityId}`
      }
    },
  },
  methods: {
    ...mapActions('users', [
      'updateProfileAndSsoData',
      'patchRegistrationData',
    ]),
    ...mapActions('orders', [
      'createOrder',
    ]),
    ...mapActions('registrations', [
      'sendRegistrationEmails',
    ]),
    priceString (price) {
      return numbro(price).format({
        thousandSeparated: true,
        mantissa: 0,
      })
    },
    redirectAfterAction () {
      let redirectRoute = this.$route.query.redirect_route ? this.$route.query.redirect_route : 'Main'
      this.$router.push({name: redirectRoute})
    },
    updateUserPaidData () {
      this.patchRegistrationData({ payment: true, paidAt: new Date(), })
    },
    saveDataAndRegister () {
      this.registrationData['productId'] = this.registrationProduct.id
      this.registrationData['registrationNumber'] = this.registrationNumber
      this.registrationData['registrationFee'] = this.registrationFeeString
      this.registrationData['registrationType'] = this.registrationProduct.name
      this.registrationData['paymentGateway'] = this.paymentGateway
      this.updateProfileAndSsoData().then(() => {
        this.createOrder({product: {
          product_id: this.registrationProduct.id,
          payment_type: this.paymentGateway,
          event_id: this.showingEventId}
        }).then(order => {
          if (order.status === 'Done') {
            // 무료인 사람들 (65세 이상)
            this.updateUserPaidData()
          } else {
            this.paymentParams = {
              pg:           this.paymentGateway,
              currency:     this.registrationProduct.price.priceUnit,
              amount:       order.price.amount,
              orderId:      order.hashedOrderId,
              orderName:    order.orderName,
              customerName: order.customerName ,
              customerPhoneNumber: this.profile.phoneNumber,
            }
            if (this.isPaymentGatewayBankTransfer) {
              this.sendRegistrationEmails({ email_type: 'myself_pending' })
            }
            this.$modal.show('registration-payment-modal')
          }
        }).catch(() => {
          this.$alert('Your payment could not be processed. If you continue to have problems, please contact support.', {
            type: 'warning'
          })
        })
      }).catch(() => {
        this.$alert('에러가 발생했습니다. 지속 발생 시, 고객센터로 문의 부탁드립니다.', {
          type: 'warning'
        })
      })
    },
    initializePaymentModal () {
      if (this.isPaymentGatewayBankTransfer) {
        this.registrationPaymentModalComponent = 'bank-transfer'
      } else {
        this.registrationPaymentModalComponent = 'iamport-payment'
      }
    },
    paymentFail (params) {
      // show payment fail with params
      this.paymentFailParams = params
      this.registrationPaymentModalComponent = 'payment-fail'
    },
    iamportSuccess (params) {
      // show payment success with params
      this.paymentSuccessParams = params
      this.registrationPaymentModalComponent = 'payment-success'
    },
    paymentFinished () {
      this.registrationPaymentModalComponent = 'payment-finished'
      // payment done
      this.updateUserPaidData()
    },
    redirectIfFinished () {
      if (this.registrationPaymentModalComponent == 'payment-finished') {
        this.redirectAfterAction()
      }
    },
    updatePhoneNumberIsValid (isValid) {
     this.invalidPhoneNumber = !isValid
    },
  },
}
</script>
